<template>
  <v-dialog max-width="800">
    <template v-slot:activator="{ props: activatorProps }">
      <v-card 
        v-bind="activatorProps" 
        @click="recordView()"
        class="mb-3"
        elevation="2"
        hover
      >
        <v-row no-gutters>
          <v-col v-if="spaceinfo" cols="auto">
            <v-img
              v-if="spaceinfo.imagename"
              :src="`https://ewr1.vultrobjects.com/crvd/cc-genealogy/${spaceinfo.spaceid}/${spaceinfo.imagename}`"
              width="120"
              height="120"
              contain
              class="rounded-s"
            >
              <template v-slot:placeholder>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-progress-circular indeterminate size="24"></v-progress-circular>
                </v-row>
              </template>
            </v-img>
            <div
              v-else
              class="d-flex align-center justify-center bg-grey-lighten-3 rounded-s"
              style="width: 120px; height: 120px;"
            >
              <v-icon size="48" color="grey-lighten-1">mdi-image-off-outline</v-icon>
            </div>
          </v-col>
          <v-col>
            <v-card-text class="pa-4">
              <v-row align="center">
                <v-col cols="12" md="6">
                  <div class="text-h6 font-weight-bold mb-1">
                    <v-icon class="mr-2" color="primary">mdi-account</v-icon>
                    {{ person.firstname }} {{ person.lastname }}
                  </div>
                  <div class="text-body-2 text-medium-emphasis">
                    <v-icon size="small" class="mr-1">mdi-map-marker</v-icon>
                    {{ person.cemeteryname }}
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="text-md-right">
                  <div v-if="person.deathday || person.deathmonth || person.deathyear" class="mb-2">
                    <v-chip 
                      prepend-icon="mdi-grave-stone" 
                      size="small"
                      color="primary"
                      variant="tonal"
                    >
                      {{ person.deathday }} {{ person.deathmonth }} {{ person.deathyear }}
                    </v-chip>
                  </div>
                  <div v-if="spaceinfo">
                    <v-chip 
                      prepend-icon="mdi-map-marker-radius"
                      size="small"
                      variant="outlined"
                    >
                      {{ spaceinfo.sectionname }}-{{ spaceinfo.lotname }}-{{ spaceinfo.spacename }}
                    </v-chip>
                    <v-chip 
                      v-if="spaceinfo.lat && spaceinfo.lon"
                      prepend-icon="mdi-map"
                      size="small"
                      color="success"
                      variant="tonal"
                      class="ml-1"
                    >
                      GPS
                    </v-chip>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-col>
        </v-row>
      </v-card>
    </template>

    <template v-slot:default="{ isActive }">
      <v-card class="rounded-lg">
        <v-card-title class="text-h5 pa-6 pb-4 d-flex align-center">
          <v-icon class="mr-3" color="primary" size="32">mdi-account-circle</v-icon>
          {{ person.firstname }} {{ person.lastname }}
        </v-card-title>
        
        <v-divider></v-divider>

        <v-card-text class="pa-6">
          <!-- Cemetery and Location Info -->
          <v-row class="mb-4">
            <v-col cols="12" md="6">
              <div class="info-section">
                <div class="text-overline text-primary font-weight-bold mb-1">
                  <v-icon size="small" class="mr-1">mdi-map-marker</v-icon>
                  Cemetery
                </div>
                <a 
                  :href="'/cemetery/' + person.slug" 
                  class="text-body-1 text-decoration-none"
                >
                  {{ person.cemeteryname }}
                  <v-icon size="small" class="ml-1">mdi-open-in-new</v-icon>
                </a>
              </div>
            </v-col>
            <v-col cols="12" md="6" v-if="spaceinfo">
              <div class="info-section">
                <div class="text-overline text-primary font-weight-bold mb-1">
                  <v-icon size="small" class="mr-1">mdi-map-marker-radius</v-icon>
                  Burial Location
                </div>
                <div class="text-body-1">
                  {{ spaceinfo.sectionname }}-{{ spaceinfo.lotname }}-{{ spaceinfo.spacename }}
                </div>
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <!-- Dates Section -->
          <v-row class="mb-4">
            <v-col cols="12" md="4">
              <div class="info-section">
                <div class="text-overline text-primary font-weight-bold mb-1">
                  <v-icon size="small" class="mr-1">mdi-cake-variant</v-icon>
                  Birth Date
                </div>
                <div class="text-body-1">
                  {{ formatDate(person.birthday, person.birthmonth, person.birthyear) }}
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="4" v-if="person.deceased">
              <div class="info-section">
                <div class="text-overline text-primary font-weight-bold mb-1">
                  <v-icon size="small" class="mr-1">mdi-grave-stone</v-icon>
                  Death Date
                </div>
                <div class="text-body-1">
                  {{ formatDate(person.deathday, person.deathmonth, person.deathyear) }}
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="4" v-if="person.deceased">
              <div class="info-section">
                <div class="text-overline text-primary font-weight-bold mb-1">
                  <v-icon size="small" class="mr-1">mdi-calendar-range</v-icon>
                  Approximate Age
                </div>
                <div class="text-body-1">
                  {{ calculateAge() }}
                </div>
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-4" v-if="person.spouse"></v-divider>

          <!-- Spouse Info -->
          <v-row class="mb-4" v-if="person.spouse">
            <v-col cols="12">
              <div class="info-section">
                <div class="text-overline text-primary font-weight-bold mb-1">
                  <v-icon size="small" class="mr-1">mdi-heart</v-icon>
                  Spouse
                </div>
                <div class="text-body-1">
                  {{ person.spouse }}
                </div>
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-4" v-if="spaceinfo && spaceinfo.imagename"></v-divider>

          <!-- Gravesite Photo -->
          <v-row v-if="spaceinfo && spaceinfo.imagename">
            <v-col cols="12">
              <div class="info-section">
                <div class="text-overline text-primary font-weight-bold mb-1">
                  <v-icon size="small" class="mr-1">mdi-image</v-icon>
                  Gravesite Photo
                </div>
                <v-card variant="outlined">
                  <v-img
                    :src="`https://ewr1.vultrobjects.com/crvd/cc-genealogy/${spaceinfo.spaceid}/${spaceinfo.imagename}`"
                    max-height="500"
                    contain
                    class="rounded"
                  >
                    <template v-slot:placeholder>
                      <v-row class="fill-height ma-0" align="center" justify="center">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                      </v-row>
                    </template>
                  </v-img>
                </v-card>
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-4" v-if="(spaceinfo && spaceinfo.lat && spaceinfo.lon) || (spaceinfo && spaceinfo.nearbylat && spaceinfo.nearbylon)"></v-divider>

          <!-- GPS Location -->
          <v-row v-if="(spaceinfo && spaceinfo.lat && spaceinfo.lon) || (spaceinfo && spaceinfo.nearbylat && spaceinfo.nearbylon)">
            <v-col cols="12">
              <div class="info-section">
                <div class="text-overline text-primary font-weight-bold mb-1">
                  <v-icon size="small" class="mr-1">mdi-map</v-icon>
                  Approximate Location
                </div>
                <v-alert v-if="spaceinfo.gpsapproximate" type="info" variant="tonal" density="compact" class="mb-2">
                  <span class="text-caption">Location shown is from a nearby space in the same lot</span>
                </v-alert>
                <v-btn
                  :href="`https://www.google.com/maps?q=${spaceinfo.lat || spaceinfo.nearbylat},${spaceinfo.lon || spaceinfo.nearbylon}`"
                  target="_blank"
                  variant="outlined"
                  size="small"
                  prepend-icon="mdi-open-in-new"
                >
                  View on Google Maps
                </v-btn>
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-4" v-if="person.publicnotes && person.publicnotes.length > 0"></v-divider>

          <!-- Public Notes -->
          <v-row v-if="person.publicnotes && person.publicnotes.length > 0">
            <v-col cols="12">
              <div class="info-section">
                <div class="text-overline text-primary font-weight-bold mb-1">
                  <v-icon size="small" class="mr-1">mdi-text-box</v-icon>
                  Public Records
                </div>
                <v-card variant="tonal" class="pa-4">
                  <div class="text-body-1">{{ person.publicnotes }}</div>
                </v-card>
              </div>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-6">
          <v-spacer />
          <v-btn
            variant="text"
            prepend-icon="mdi-share-variant"
            @click="shareRecord"
          >
            Share
          </v-btn>
          <AddChangeRequestDialog :person="person" />
          <v-btn 
            variant="outlined"
            @click="isActive.value = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </template>
  </v-dialog>

  <v-snackbar v-model="snackbar" :timeout="3000">
    Link copied to clipboard!
    <template v-slot:actions>
      <v-btn color="primary" variant="text" @click="snackbar = false">
        Close
      </v-btn>
    </template>
  </v-snackbar>
</template>

<script>
import AddChangeRequestDialog from './AddChangeRequestDialog.vue';

export default {
  name: 'PersonDialog',

  components : {
    AddChangeRequestDialog
  },

  props:{
    person : Object,
    cemeteries : Array
  },

  data: () => ({
    spaceinfo : null,
    snackbar: false
  }),

  created : function(){
    fetch("https://app.cemeterycommunity.com:11000/api/getspacebyburialid", { method: "POST", headers : { data : JSON.stringify({ burialid : this.person.id }) } })
      .then( res => res.json() )
      .then( res =>  {
        if(res.results == "Failed" ) return;
        else this.spaceinfo = res.results[0];
      });
  },

  methods:{
    formatDate(day, month, year){
      if(!day && !month && !year) return 'Unknown';
      return `${day || ''} ${month || ''} ${year || ''}`.trim();
    },
    calculateAge(){
      if(!this.person.birthyear || !this.person.deathyear) return 'N/A';
      const age = this.person.deathyear - this.person.birthyear;
      if(age > 110 || age < 0) return 'N/A';
      return age;
    },
    recordView(){
      fetch("https://app.cemeterycommunity.com:11000/api/viewrecord", { method: "POST", headers : { data : JSON.stringify( { cemeteryid : this.person.cemeteryid, search : this.person.firstname + ' ' + this.person.lastname } ) } });
    },
    shareRecord(){
      const url = window.location.origin + '/person?personid=' + this.person.id;
      navigator.clipboard.writeText(url).then(() => {
        this.snackbar = true;
      }).catch(err => {
        console.error('Failed to copy link:', err);
      });
    }
  }
}
</script>